// User-defined clusters, saved in localStorage by game id so they survive
// data rebuilds. A game belongs to at most one user cluster.
import { useCallback, useEffect, useState } from "react";
import { ALL_FACTORS, TAG_KINDS, scorePair } from "./graphModel";

const STORAGE_KEY = "ludograph.userClusters";

function readStored() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((c) => c && typeof c.id === "string" && Array.isArray(c.members))
      .map((c) => ({ id: c.id, name: String(c.name || "Untitled cluster"), members: c.members.map(String) }));
  } catch {
    return [];
  }
}

const without = (c, ids) => {
  const members = c.members.filter((m) => !ids.includes(m));
  return members.length === c.members.length ? c : { ...c, members };
};

export function useUserClusters() {
  const [clusters, setClusters] = useState(readStored);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(clusters));
    } catch {
      // Storage may be unavailable (private window); clusters stay in memory.
    }
  }, [clusters]);

  // Keep other open tabs in sync.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setClusters(readStored());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Creating a cluster moves the given games out of any other cluster.
  const create = useCallback((name, gameIds = []) => {
    const id = `uc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    setClusters((cs) => [...cs.map((c) => without(c, gameIds)), { id, name, members: [...gameIds] }]);
    return id;
  }, []);

  // Moves a game into `clusterId`, or out of every cluster when it is null.
  const assign = useCallback((gameId, clusterId) => {
    setClusters((cs) =>
      cs.map((c) => {
        if (c.id !== clusterId) return without(c, [gameId]);
        return c.members.includes(gameId) ? c : { ...c, members: [...c.members, gameId] };
      })
    );
  }, []);

  const rename = useCallback((clusterId, name) => {
    setClusters((cs) => cs.map((c) => (c.id === clusterId ? { ...c, name } : c)));
  }, []);

  const remove = useCallback((clusterId) => {
    setClusters((cs) => cs.filter((c) => c.id !== clusterId));
  }, []);

  return { clusters, create, assign, rename, remove };
}

// Attaches game indices to stored clusters and maps each game to its cluster.
export function resolveUserClusters(stored, model) {
  const byId = Object.fromEntries(model.games.map((g) => [g.id, g.index]));
  const clusterOf = new Array(model.n).fill(null);
  const list = stored.map((c) => {
    const members = c.members.map((id) => byId[id]).filter((i) => i != null);
    members.forEach((i) => (clusterOf[i] = c.id));
    return { ...c, members, size: members.length };
  });
  return { list, byId: Object.fromEntries(list.map((c) => [c.id, c])), clusterOf };
}

// Unclustered games ranked by their average link strength to every member,
// so suggestions fit the cluster as a whole rather than one game in it.
// With `includeOtherClusters`, games in other user clusters are candidates
// too, and `from` names the cluster each one is in.
export function suggestForCluster(
  model,
  members,
  clusterOf,
  weights,
  seriesWeight,
  { includeOtherClusters = false, limit = 8 } = {}
) {
  if (!members.length) return [];
  const { n, pairIndex } = model;
  const inside = new Set(members);
  const out = [];
  for (let i = 0; i < n; i++) {
    if (inside.has(i) || (clusterOf[i] != null && !includeOtherClusters)) continue;
    let sum = 0;
    let closest = members[0];
    let closestScore = -1;
    members.forEach((m) => {
      const { s } = scorePair(pairIndex[i * n + m], weights, seriesWeight);
      sum += s;
      if (s > closestScore) {
        closestScore = s;
        closest = m;
      }
    });
    out.push({ i, s: sum / members.length, closest, from: clusterOf[i] });
  }
  return out.sort((a, b) => b.s - a.s).slice(0, limit);
}

// User clusters (other than the game's own) that the game has drawn links
// into, ranked by the total strength of those links. `fit` is the game's
// average score against every member, as in suggestForCluster.
export function recommendClusters(model, graph, userClusters, index, weights, seriesWeight, limit = 3) {
  const { n, pairIndex } = model;
  const own = userClusters.clusterOf[index];
  const byCluster = {};
  graph.adj[index].forEach(({ o, e }) => {
    const id = userClusters.clusterOf[o];
    if (id == null || id === own) return;
    const r = (byCluster[id] = byCluster[id] || { id, links: 0, total: 0, closest: o, closestScore: -1 });
    r.links += 1;
    r.total += e.s;
    if (e.s > r.closestScore) {
      r.closestScore = e.s;
      r.closest = o;
    }
  });
  return Object.values(byCluster)
    .map((r) => {
      const cluster = userClusters.byId[r.id];
      const fit =
        cluster.members.reduce((sum, m) => sum + scorePair(pairIndex[index * n + m], weights, seriesWeight).s, 0) /
        cluster.members.length;
      return { ...r, cluster, fit };
    })
    .sort((a, b) => b.total - a.total)
    .slice(0, limit);
}

// Numbers for judging whether a cluster's games belong together: how strongly
// members link to each other compared with the collection as a whole, which
// factors drive those links, which members fit worst, and which tags most
// members share. Returns null for clusters with fewer than two games.
export function clusterStats(model, graph, members, weights, seriesWeight) {
  if (members.length < 2) return null;
  const { n, pairs, pairIndex, games } = model;
  const baseline = pairs.reduce((sum, p) => sum + scorePair(p, weights, seriesWeight).s, 0) / (pairs.length || 1);

  const inside = new Set(members);
  const fit = Object.fromEntries(members.map((m) => [m, 0]));
  const contrib = Object.fromEntries(ALL_FACTORS.map((f) => [f.key, 0]));
  let total = 0;
  let pairCount = 0;
  members.forEach((a, ai) =>
    members.slice(ai + 1).forEach((b) => {
      const r = scorePair(pairIndex[a * n + b], weights, seriesWeight);
      total += r.s;
      fit[a] += r.s;
      fit[b] += r.s;
      ALL_FACTORS.forEach((f) => (contrib[f.key] += r.contrib[f.key]));
      pairCount += 1;
    })
  );
  const cohesion = total / pairCount;
  // Share of the average link each factor accounts for, so the bar fills to 100%.
  const share = Object.fromEntries(ALL_FACTORS.map((f) => [f.key, total ? contrib[f.key] / total : 0]));

  const linked = graph.edges.filter((e) => inside.has(e.a) && inside.has(e.b)).length;

  const fits = members
    .map((i) => ({ i, fit: fit[i] / (members.length - 1) }))
    .sort((a, b) => a.fit - b.fit);

  const sharedTags = TAG_KINDS.map(({ kind, label, format }) => {
    const counts = {};
    members.forEach((i) => games[i][kind].forEach((t) => (counts[t] = (counts[t] || 0) + 1)));
    const items = Object.keys(counts)
      .filter((t) => counts[t] >= Math.max(2, members.length / 2))
      .sort((a, b) => counts[b] - counts[a] || a.localeCompare(b))
      .slice(0, 8);
    return { kind, label, format, counts, items };
  });

  return { cohesion, baseline, lift: baseline ? cohesion / baseline : 0, linked, pairCount, share, fits, sharedTags };
}
